import { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { FileUploader } from 'react-drag-drop-files';
import { fetchPortfolioProjects, fetchPortfolioProjectsAndOptions, deleteProject, bulkEditProjects, updateProject, createProject, createProjectFromPdf, searchClipPublications, reorderProjects, uploadProjectImage, deleteProjectImage, searchBehanceProjects } from './portfolioApi';
import type { BehanceProject } from './portfolioApi';
import type { ClipPublicationSearchResult } from './portfolioApi';
import type { PortfolioProject, PortfolioProjectOptions, ProjectTagOption } from './types';
import EditPortfolioLayout from './components/EditPortfolioLayout';
import { logError } from '../../components/utils/errors';

const IMAGE_FILE_TYPES = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'];
const PDF_FILE_TYPES = ['PDF'];

// Matches the backend Portfolios::ProjectsController PER_PAGE. The page loads one
// "page" of projects at a time and reveals more via a "Load more" button, mirroring
// the legacy projects_collection the React migration dropped (eng-maintenance#21323).
const PROJECTS_PER_PAGE = 100;

const C = { teal: '#5bc0be', salmon: '#e8927c', red: '#c0392b', border: '#e6e8eb', borderDark: '#d4d8dd', text: '#333', textSec: '#666', muted: '#999', bg: '#f6f7f8', white: '#fff' };

const PageTitle = styled.h1`font-size: 28px; font-weight: 300; color: ${C.salmon}; margin: 0 0 8px;`;
const Help = styled.h3`font-size: 15px; font-weight: 400; color: ${C.textSec}; margin: 0 0 24px; line-height: 1.5; a { color: ${C.teal}; text-decoration: none; &:hover { text-decoration: underline; } }`;
const Toolbar = styled.div`display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid ${C.border};`;
const Chk = styled.input.attrs({ type: 'checkbox' })`margin: 0; cursor: pointer;`;
const DropWrap = styled.div`position: relative;`;
const DropBtn = styled.button<{ $p?: boolean }>`
  padding: 6px 14px; border: 1px solid ${({ $p }) => $p ? C.teal : C.borderDark};
  background: ${C.white}; color: ${({ $p }) => $p ? C.teal : C.textSec};
  font-size: 11px; text-transform: uppercase; letter-spacing: .5px; border-radius: 3px;
  cursor: pointer; font-weight: 500; display: flex; align-items: center; gap: 4px;
  &::after { content: '▾'; font-size: 9px; }
  &:hover { background: ${({ $p }) => $p ? C.teal : C.bg}; color: ${({ $p }) => $p ? C.white : C.text}; }
`;
const DropMenu = styled.ul<{ $v: boolean }>`
  display: ${({ $v }) => $v ? 'block' : 'none'};
  position: absolute; top: 100%; left: 0; background: ${C.white};
  border: 1px solid ${C.border}; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,.1);
  list-style: none; padding: 4px 0; margin: 4px 0 0; min-width: 200px; z-index: 100;
`;
const DropItem = styled.li`padding: 8px 16px; font-size: 13px; color: ${C.text}; cursor: pointer; &:hover { background: ${C.bg}; }`;
const ToolRight = styled.div`margin-left: auto;`;

// "Load more" pagination control (eng-maintenance#21323) — restores the legacy
// projects page's load-more button (classic pagination_button.js.hamlc, a
// `.button.secondary.small`). Shown only while more pages remain.
const LoadMoreWrap = styled.div`display: flex; justify-content: center; padding: 20px 0;`;
const LoadMoreBtn = styled.button`
  padding: 8px 24px; border: 1px solid ${C.borderDark}; background: ${C.white};
  color: ${C.textSec}; font-size: 12px; text-transform: uppercase; letter-spacing: .5px;
  border-radius: 3px; cursor: pointer; font-weight: 500;
  &:hover { background: ${C.bg}; color: ${C.text}; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const ProjRow = styled.div<{ $isDragging?: boolean }>`
  border-bottom: 1px solid ${C.border}; padding: 10px 0;
  background: ${({ $isDragging }) => $isDragging ? C.bg : 'transparent'};
`;
const ProjHead = styled.div`display: flex; align-items: center; gap: 10px;`;
const DragHandle = styled.span`
  cursor: grab; color: ${C.muted}; font-size: 16px; padding: 4px; user-select: none;
  display: flex; align-items: center;
  &:hover { color: ${C.text}; }
  &:active { cursor: grabbing; }
`;
const Expand = styled.button<{ $e: boolean }>`
  background: none; border: none; cursor: pointer; font-size: 12px; color: ${C.muted}; padding: 4px;
  transform: ${({ $e }) => $e ? 'rotate(90deg)' : 'none'}; transition: transform .15s;
`;
const ProjInfo = styled.div`flex: 1; min-width: 0;`;
const ProjName = styled.span`font-size: 14px; font-weight: 600; color: ${C.text}; display: block;`;
const ProjSrc = styled.span`font-size: 12px; color: ${C.muted};`;
const ProjAct = styled.div`display: flex; align-items: center; gap: 8px;`;
const IcoBtn = styled.button`background: none; border: none; cursor: pointer; color: ${C.muted}; font-size: 14px; padding: 4px; &:hover { color: ${C.text}; }`;
const VisDrop = styled.div`position: relative;`;
const VisBtn = styled.button`
  background: none; border: none; cursor: pointer; color: ${C.muted}; font-size: 13px; padding: 4px;
  display: flex; align-items: center; gap: 2px; &::after { content: '▾'; font-size: 9px; } &:hover { color: ${C.text}; }
`;
const VisMenu = styled.ul<{ $v: boolean }>`
  display: ${({ $v }) => $v ? 'block' : 'none'}; position: absolute; top: 100%; right: 0;
  background: ${C.white}; border: 1px solid ${C.border}; border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,.1); list-style: none; padding: 4px 0; margin: 4px 0 0;
  min-width: 140px; z-index: 100;
`;
const VisItem = styled.li<{ $a?: boolean }>`
  padding: 6px 16px; font-size: 13px; color: ${({ $a }) => $a ? C.teal : C.text};
  cursor: pointer; font-weight: ${({ $a }) => $a ? 600 : 400}; &:hover { background: ${C.bg}; }
`;
const Expanded = styled.div`padding: 12px 0 12px 36px;`;
const ExpSec = styled.div`margin-bottom: 12px;`;
const ExpLabel = styled.h6`font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: ${C.muted}; margin: 0 0 6px;`;
const ExpLink = styled.a`font-size: 13px; color: ${C.teal}; text-decoration: none; &:hover { text-decoration: underline; } &::after { content: ' ↗'; font-size: 11px; }`;
const ExpImg = styled.img`max-width: 120px; max-height: 90px; border-radius: 3px; object-fit: cover;`;
// Plain-text expanded-row value (Format / Description / Topics / Skills) —
// eng-maintenance#21110, matching the classic page's plain-text display rows.
const ExpText = styled.span`font-size: 13px; color: ${C.text}; white-space: pre-wrap;`;
// PDF-backed clip file reference (eng-maintenance#21094) — file icon + filename
// as plain text, mirroring legacy classic `.file-icon-w-name` (no link).
const PdfFile = styled.span`display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: ${C.text};`;
const PdfIcon = styled.span`font-size: 14px; line-height: 1;`;

const Modal = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1000;`;
const ModalBox = styled.div`background: ${C.white}; border-radius: 6px; padding: 24px; width: 500px; max-width: 90%; max-height: 80vh; overflow-y: auto;`;
const ModalTitle = styled.h2`font-size: 20px; font-weight: 400; color: ${C.salmon}; margin: 0 0 20px;`;
const ModalLabel = styled(ExpLabel)`color: ${C.textSec}; margin-bottom: 6px;`;
const ModalInput = styled.input`width: 100%; padding: 8px 12px; border: 1px solid ${C.borderDark}; border-radius: 3px; font-size: 14px; color: ${C.text}; box-sizing: border-box; margin-bottom: 16px; &:focus { outline: none; border-color: ${C.teal}; } &::placeholder { color: #bbb; }`;
const ModalTextarea = styled.textarea`width: 100%; padding: 8px 12px; border: 1px solid ${C.borderDark}; border-radius: 3px; font-size: 14px; color: ${C.text}; box-sizing: border-box; margin-bottom: 16px; min-height: 60px; resize: vertical; &:focus { outline: none; border-color: ${C.teal}; }`;
const ModalBtns = styled.div`display: flex; justify-content: flex-end; gap: 12px; margin-top: 16px;`;
// parity-audit-r2 (L6): Behance search-results picker list.
const BehanceResultList = styled.div`max-height: 240px; overflow-y: auto; border: 1px solid ${C.borderDark}; border-radius: 3px;`;
const BehanceResultItem = styled.label`display: flex; align-items: center; gap: 10px; padding: 8px 12px; font-size: 14px; color: ${C.text}; cursor: pointer; border-bottom: 1px solid ${C.border}; &:last-child { border-bottom: none; } &:hover { background: ${C.bg}; }`;
const MBtn = styled.button<{ $p?: boolean }>`
  padding: 8px 20px; border: 1px solid ${({ $p }) => $p ? C.teal : C.borderDark};
  background: ${({ $p }) => $p ? C.teal : C.white}; color: ${({ $p }) => $p ? C.white : C.textSec};
  font-size: 13px; border-radius: 3px; cursor: pointer; &:hover { opacity: .9; } &:disabled { opacity: .5; cursor: not-allowed; }
`;
const UseImageRow = styled.label`display: flex; align-items: center; gap: 8px; margin-bottom: 16px; cursor: pointer; font-size: 14px; color: ${C.text};`;
const ImageGrid = styled.div`display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 12px;`;
const ImageThumb = styled.div`position: relative; width: 80px; height: 80px;`;
const ThumbImg = styled.img`width: 80px; height: 80px; border-radius: 3px; object-fit: cover; border: 1px solid ${C.border};`;
const ThumbDel = styled.button`
  position: absolute; top: -8px; right: -8px; width: 20px; height: 20px; border-radius: 50%;
  border: none; background: ${C.red}; color: ${C.white}; font-size: 12px; line-height: 1; cursor: pointer;
  display: flex; align-items: center; justify-content: center; &:hover { opacity: .85; } &:disabled { opacity: .5; cursor: not-allowed; }
`;
const Dropzone = styled.div`
  margin-bottom: 16px;
  label { display: block; }
  label > div { border: 2px dashed ${C.borderDark}; border-radius: 4px; min-width: 100%; max-width: 100%;
    padding: 18px; text-align: center; color: ${C.textSec}; background: ${C.bg}; }
`;

// Manual-entry fallback (eng-maintenance#20455). The notice mirrors classic's
// flash copy; the URL is shown read-only as a link (classic rendered it as a
// read-only `%a.blank` — the URL is set at import time and not editable here).
const ManualNotice = styled.p`font-size: 14px; color: ${C.textSec}; margin: 0 0 20px; line-height: 1.4;`;
const ManualUrl = styled.a`
  display: block; margin-bottom: 16px; font-size: 14px; color: ${C.teal};
  word-break: break-all; &:hover { color: ${C.salmon}; }
`;

// Client typeahead (eng-maintenance#21068). Anchors the results dropdown to the
// input; results overlay the modal body without pushing layout.
const ClientSearch = styled.div`position: relative; margin-bottom: 16px;`;
const ClientResults = styled.ul`
  position: absolute; top: 100%; left: 0; right: 0; z-index: 2; margin: 2px 0 0; padding: 0; list-style: none;
  max-height: 180px; overflow-y: auto; background: ${C.white}; border: 1px solid ${C.borderDark};
  border-radius: 3px; box-shadow: 0 2px 8px rgba(0,0,0,.12);
`;
const ClientResultItem = styled.li`padding: 8px 12px; font-size: 14px; color: ${C.text}; cursor: pointer; &:hover { background: ${C.bg}; }`;
const ClientChosen = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 16px;
  padding: 8px 12px; border: 1px solid ${C.borderDark}; border-radius: 3px; font-size: 14px; color: ${C.text}; background: ${C.bg};
`;
const LinkBtn = styled.button`
  background: none; border: none; padding: 0; color: ${C.teal}; font-size: 13px; cursor: pointer;
  text-decoration: underline; &:hover { color: ${C.salmon}; }
`;

// Skills/Topics/Format edit controls (eng-maintenance#21110). Format is a single
// <select> over the StoryFormat catalog; Skills/Topics are searchable multi-selects
// (chips for the current selection + a filtered dropdown), mirroring classic's
// select2 widgets which loaded the full option list client-side.
const ModalSelect = styled.select`
  width: 100%; padding: 8px 12px; border: 1px solid ${C.borderDark}; border-radius: 3px;
  font-size: 14px; color: ${C.text}; box-sizing: border-box; margin-bottom: 16px; background: ${C.white};
  &:focus { outline: none; border-color: ${C.teal}; }
`;
const TagChips = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px;`;
const TagChip = styled.span`
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px;
  background: ${C.bg}; border: 1px solid ${C.borderDark}; border-radius: 12px; font-size: 12px; color: ${C.text};
`;
const TagChipX = styled.button`
  background: none; border: none; padding: 0; cursor: pointer; color: ${C.muted}; font-size: 14px; line-height: 1;
  &:hover { color: ${C.red}; }
`;

const Toast = styled.div<{ $ok: boolean }>`
  position: fixed; bottom: 24px; right: 24px; padding: 12px 20px; border-radius: 4px;
  font-size: 14px; font-weight: 500; z-index: 1001;
  background: ${({ $ok }) => $ok ? C.teal : C.red}; color: ${C.white};
`;

const Empty = styled.div`text-align: center; padding: 40px 0; color: ${C.muted}; font-size: 14px;`;

function extractDomain(url: string): string {
  try { return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, ''); } catch { return ''; }
}

// Searchable multi-select for project Skills/Topics (eng-maintenance#21110).
// Selected items render as removable chips; typing filters the remaining options.
function TagMultiSelect({ label, options, selectedIds, onChange }: {
  label: string;
  options: ProjectTagOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const selected = options.filter(o => selectedIds.includes(o.id));
  const t = term.trim().toLowerCase();
  const matches = options
    .filter(o => !selectedIds.includes(o.id))
    .filter(o => !t || o.name.toLowerCase().includes(t))
    .slice(0, 50);
  const add = (id: number) => { onChange([...selectedIds, id]); setTerm(''); setOpen(false); };
  const remove = (id: number) => onChange(selectedIds.filter(x => x !== id));
  return (
    <>
      <ModalLabel>{label}</ModalLabel>
      {selected.length > 0 && (
        <TagChips>
          {selected.map(o => (
            <TagChip key={o.id}>{o.name}<TagChipX type="button" title={`Remove ${o.name}`} onClick={() => remove(o.id)}>×</TagChipX></TagChip>
          ))}
        </TagChips>
      )}
      <ClientSearch>
        <ModalInput
          placeholder={`Start typing to add ${label.toLowerCase()}`}
          value={term}
          onChange={e => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          style={{ marginBottom: 0 }}
        />
        {open && matches.length > 0 && (
          <ClientResults>
            {matches.map(o => (
              <ClientResultItem key={o.id} onClick={() => add(o.id)}>{o.name}</ClientResultItem>
            ))}
          </ClientResults>
        )}
      </ClientSearch>
    </>
  );
}

function PortfolioProjectsPage() {
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  // Total projects across all pages (from the `/portfolio/projects.json` meta).
  // The "Load more" button shows while fewer than this many are loaded — legacy
  // parity (classic projects_collection: hasNextPage = total_count > size()).
  // eng-maintenance#21323.
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // How many pages are currently loaded on screen. A ref (not state) so `load`
  // stays a stable useCallback([]) — it's read inside load()/loadMore() and must
  // NOT be a dependency, or the mount effect `useEffect(()=>{load()},[load])`
  // would refetch in a loop. Post-mutation refreshes re-fetch this same breadth so
  // a user who paged past 100 isn't snapped back to the first page after a
  // delete/edit/reorder. eng-maintenance#21323.
  const loadedPagesRef = useRef(1);
  // Synchronous guard against overlapping "Load more" fetches. The `loadingMore`
  // state below drives the button's disabled UI, but state updates lag a tick, so
  // a rapid double-click could pass the `if (loadingMore)` check twice, bump
  // `loadedPagesRef` twice, and start two `load()`s with different limits — if the
  // smaller response resolves last, `setProjects` would drop already-loaded rows
  // (Bugbot, eng-maintenance#21323). A ref is synchronous so the second click is
  // dropped before the request starts — same pattern as `reorderInFlight` below.
  const loadMoreInFlight = useRef(false);
  // Guards against firing a second reorder POST while the first is still in
  // flight. Two concurrent same-user POSTs to /portfolio/projects/sort
  // deadlocked the backend (HB 131767363); a ref is synchronous so a rapid
  // second drag is dropped before the request starts (state would lag a tick).
  const reorderInFlight = useRef(false);
  // Tracks whether the URL-import flow is still active. If the user cancels/
  // closes the import modal while createProject is in flight, a later 503 must
  // NOT pop the manual-entry fallback open over a dismissed flow. A ref is
  // synchronous so the check in the async catch sees the dismissal immediately
  // (state would lag a tick) — same pattern as reorderInFlight above.
  const importDismissed = useRef(false);
  // Same guard for the manual-save step (eng-maintenance#20455): if the user
  // cancels the manual-entry modal while its createProject is in flight, a later
  // resolve/reject must not toast over the dismissed flow.
  const manualDismissed = useRef(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actOpen, setActOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [visOpenId, setVisOpenId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [modal, setModal] = useState<'url' | 'behance' | 'pdf' | 'edit' | 'manual' | null>(null);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  // parity-audit-r2 (L6): Behance import — username search → select → import.
  const [behanceUsername, setBehanceUsername] = useState('');
  const [behanceSearching, setBehanceSearching] = useState(false);
  const [behanceResults, setBehanceResults] = useState<BehanceProject[] | null>(null);
  const [behanceSelectedIds, setBehanceSelectedIds] = useState<Set<number>>(new Set());
  const [behanceImporting, setBehanceImporting] = useState(false);
  // Manual-entry fallback (eng-maintenance#20455). When URL auto-extraction
  // fails fast (503), classic dropped the user into a manual-entry card seeded
  // with the URL so they could still create the project by hand. The React
  // migration dropped that branch; these fields restore it (URL read-only +
  // Title/Format/Description/Topics/Skills, matching classic's edit card).
  const [manualUrl, setManualUrl] = useState('');
  const [manualTitle, setManualTitle] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualStoryFormat, setManualStoryFormat] = useState('');
  const [manualTopicIds, setManualTopicIds] = useState<number[]>([]);
  const [manualSkillIds, setManualSkillIds] = useState<number[]>([]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfTitle, setPdfTitle] = useState('');
  // Import-a-PDF "Client" section (eng-maintenance#21068). Legacy required either
  // an existing client (clipPublicationId) or a new one (name + homepage URL).
  const [pdfClient, setPdfClient] = useState<ClipPublicationSearchResult | null>(null);
  const [pdfClientTerm, setPdfClientTerm] = useState('');
  const [pdfClientResults, setPdfClientResults] = useState<ClipPublicationSearchResult[]>([]);
  const [pdfClientOpen, setPdfClientOpen] = useState(false);
  const [pdfNewClient, setPdfNewClient] = useState(false);
  const [pdfNewClientName, setPdfNewClientName] = useState('');
  const [pdfNewClientUrl, setPdfNewClientUrl] = useState('');
  const [editProject, setEditProject] = useState<PortfolioProject | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editUseImage, setEditUseImage] = useState(false);
  const [editAssets, setEditAssets] = useState<Array<{ id: number; small: string }>>([]);
  const [imgBusy, setImgBusy] = useState(false);
  // Skills/Topics/Format edit state + the selectable option catalog
  // (eng-maintenance#21110). Options are fetched alongside the project list.
  const [options, setOptions] = useState<PortfolioProjectOptions>({ skills: [], topics: [], storyFormats: [] });
  const [editStoryFormat, setEditStoryFormat] = useState('');
  const [editSkillIds, setEditSkillIds] = useState<number[]>([]);
  const [editTopicIds, setEditTopicIds] = useState<number[]>([]);

  const load = useCallback(async () => {
    try {
      // Re-fetch the breadth currently on screen in ONE request (backend `limit`
      // override) so a refresh after any mutation keeps the user where they paged
      // to, instead of collapsing back to the first 100. eng-maintenance#21323.
      const data = await fetchPortfolioProjectsAndOptions({ limit: loadedPagesRef.current * PROJECTS_PER_PAGE });
      // Clamp to what actually came back — a bulk delete can shrink the set below
      // the loaded breadth, so we must not keep claiming more pages than exist.
      loadedPagesRef.current = Math.max(1, Math.ceil(data.projects.length / PROJECTS_PER_PAGE));
      setProjects(data.projects);
      setOptions(data.options);
      setTotalCount(data.meta.totalCount);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Reveal the next page of projects, appended below the current list. Mirrors the
  // legacy "Load more" button (classic pagination_button_view -> collection.fetchMore,
  // which requested page floor(size/100)+1). Implemented as a breadth re-fetch via
  // `load()` so reorders/deletes stay consistent and ids never duplicate.
  // eng-maintenance#21323.
  const loadMore = useCallback(async () => {
    // Synchronous ref check — drops a rapid second click before it can
    // double-increment the page ref or race a second fetch (Bugbot #21323).
    if (loadMoreInFlight.current) return;
    loadMoreInFlight.current = true;
    setLoadingMore(true);
    loadedPagesRef.current += 1;
    try {
      await load();
    } finally {
      setLoadingMore(false);
      loadMoreInFlight.current = false;
    }
  }, [load]);
  useEffect(() => { const h = () => { setActOpen(false); setAddOpen(false); setVisOpenId(null); }; document.addEventListener('click', h); return () => document.removeEventListener('click', h); }, []);

  const flash = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); };

  const handleSelectAll = (c: boolean) => setSelected(c ? new Set(projects.map(p => p.id)) : new Set());
  const handleSelect = (id: number, c: boolean) => setSelected(prev => { const n = new Set(prev); c ? n.add(id) : n.delete(id); return n; });

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try { await deleteProject(id); flash('Project deleted', true); load(); } catch { flash('Failed to delete', false); }
  };

  const handleBulk = async (action: string) => {
    if (selected.size === 0) return;
    setActOpen(false);
    if (action === 'delete' && !window.confirm(`Delete ${selected.size} selected project(s)?`)) return;
    try { await bulkEditProjects(action, Array.from(selected)); flash(`Action "${action}" applied`, true); setSelected(new Set()); load(); } catch { flash('Action failed', false); }
  };

  const handleVisibility = async (id: number, pub: boolean) => {
    setVisOpenId(null);
    try { await updateProject(id, { public: pub }); flash(pub ? 'Visible to everyone' : 'Visible to Contently only', true); load(); } catch { flash('Failed to update', false); }
  };

  // Closing the import modal (Cancel / backdrop) marks the flow dismissed so an
  // in-flight createProject that later rejects with a 503 won't pop the
  // manual-entry fallback back open over a dismissed flow.
  const closeImportModal = () => { importDismissed.current = true; setModal(null); };

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    const url = importUrl.trim();
    importDismissed.current = false;
    setImporting(true);
    try {
      await createProject({ url });
      // If the user dismissed the modal mid-flight, the project still created —
      // refresh the list but don't re-toast over whatever they're now doing.
      if (importDismissed.current) { load(); return; }
      flash('Project created from URL', true); setImportUrl(''); setModal(null); load();
    } catch (e: any) {
      // The user already walked away from this import — don't resurrect it.
      if (importDismissed.current) return;
      // Auto-extraction failed. The backend fails fast with 503 when Diffbot/
      // image extraction can't parse the URL (eng-maintenance#19526); a gateway
      // timeout surfaces as 504. Classic routed exactly `503 || 504` into a
      // manual-entry form seeded with the URL instead of an error toast
      // (eng-maintenance#20455 — new_project_view.js.coffee). Match that trigger
      // exactly: any other failure (a validation 500, a network/abort error with
      // no response) keeps the original generic toast — don't broaden the
      // fallback beyond classic.
      const status = e.response?.status;
      if (status === 503 || status === 504) {
        setManualUrl(url);
        setManualTitle(''); setManualDescription(''); setManualStoryFormat('');
        setManualTopicIds([]); setManualSkillIds([]);
        setImportUrl('');
        setModal('manual');
      } else {
        flash(e.response?.data?.errors?.[0] || 'Import failed', false);
      }
    }
    finally { setImporting(false); }
  };

  // parity-audit-r2 (L6): Behance import flow — mirror legacy
  // (import_via_external_service_view.js.coffee): search the user's Behance
  // projects by username, let them pick which to import, then create each via
  // `behance_id` (BE → BehanceClipBuilder). Replaces the migrated stub that POSTed
  // a raw URL (which silently fell through to the generic Diffbot scrape).
  const closeBehanceModal = () => {
    setModal(null);
    setBehanceUsername(''); setBehanceResults(null);
    setBehanceSelectedIds(new Set()); setBehanceSearching(false); setBehanceImporting(false);
  };

  const handleBehanceSearch = async () => {
    const username = behanceUsername.trim();
    if (!username) return;
    setBehanceSearching(true);
    setBehanceResults(null);
    setBehanceSelectedIds(new Set());
    try {
      const projects = await searchBehanceProjects(username);
      setBehanceResults(projects);
      if (projects.length === 0) {
        flash('Sorry, we were unable to find any projects for that username.', false);
      }
    } catch (e: any) {
      // Legacy distinguished 404 (no such user) from a generic Behance fetch error.
      const status = e.response?.status;
      flash(
        status === 404
          ? 'No user was found with that username.'
          : 'Sorry, there was a problem fetching projects from Behance.',
        false,
      );
      setBehanceResults(null);
    } finally {
      setBehanceSearching(false);
    }
  };

  const toggleBehanceSelected = (id: number) => {
    setBehanceSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBehanceImport = async () => {
    if (!behanceResults || behanceSelectedIds.size === 0) return;
    const selected = behanceResults.filter((p) => behanceSelectedIds.has(p.id));
    setBehanceImporting(true);
    // Create each selected project via its behance_id (legacy created one clip per
    // picked project). Sequential keeps ordering stable and avoids hammering the
    // Behance API behind BehanceClipBuilder. Accumulate per-item outcomes rather
    // than aborting on the first failure — otherwise a mid-batch error would leave
    // earlier projects silently created with no indication of partial success.
    let ok = 0;
    for (const p of selected) {
      try {
        await createProject({ behance_id: p.id, title: p.title });
        ok += 1;
      } catch {
        // keep going — report the tally below
      }
    }
    setBehanceImporting(false);
    const failed = selected.length - ok;
    if (ok > 0) {
      flash(
        failed === 0
          ? `Imported ${ok} project${ok === 1 ? '' : 's'} from Behance`
          : `Imported ${ok} of ${selected.length} projects (${failed} failed)`,
        failed === 0,
      );
      closeBehanceModal();
      load(); // refresh so the successfully-imported projects appear
    } else {
      flash('Behance import failed', false);
    }
  };

  // Save the manually-entered project (eng-maintenance#20455 fallback). With url
  // + title both present the backend skips Diffbot and derives the publication
  // from the URL domain, so this is the same create endpoint — no BE change.
  // All fields are required (parity with classic's manual card).
  const manualValid =
    manualTitle.trim().length > 0 &&
    manualDescription.trim().length > 0 &&
    manualStoryFormat !== '' &&
    manualTopicIds.length > 0 &&
    manualSkillIds.length > 0;

  // Closing the manual modal marks the flow dismissed (mirrors closeImportModal)
  // so an in-flight manual save that later resolves/rejects won't toast over it.
  const closeManualModal = () => { manualDismissed.current = true; setModal(null); };

  const handleManualSave = async () => {
    if (!manualValid) return;
    manualDismissed.current = false;
    setImporting(true);
    try {
      await createProject({
        url: manualUrl,
        title: manualTitle.trim(),
        description: manualDescription.trim(),
        story_format: manualStoryFormat,
        topic_ids: manualTopicIds,
        skill_ids: manualSkillIds,
      });
      // User walked away mid-flight — refresh the list but don't re-toast/close.
      if (manualDismissed.current) { load(); return; }
      flash('Project created', true); setModal(null); load();
    } catch (e: any) {
      if (manualDismissed.current) return;
      flash(e.response?.data?.errors?.[0] || 'Failed to create project', false);
    }
    finally { setImporting(false); }
  };

  const closePdfModal = () => {
    setModal(null); setPdfFile(null); setPdfTitle('');
    setPdfClient(null); setPdfClientTerm(''); setPdfClientResults([]); setPdfClientOpen(false);
    setPdfNewClient(false); setPdfNewClientName(''); setPdfNewClientUrl('');
  };

  // Mirror handleImageUpload's signature: react-drag-drop-files passes a single
  // File when `multiple` is unset (a FileList only when multiple). Typed File |
  // FileList to match the library's runtime contract.
  const handlePdfSelect = (fileOrList: File | FileList) => {
    const file = fileOrList instanceof FileList ? fileOrList[0] : fileOrList;
    if (file) setPdfFile(file);
  };

  // Debounced client typeahead — queries /clip_publications/autocomplete.json
  // (the same source the legacy select2 widget used). Only runs while the
  // existing-client picker is open and a non-trivial term is typed.
  useEffect(() => {
    if (pdfNewClient || pdfClient || pdfClientTerm.trim().length < 2) { setPdfClientResults([]); return; }
    // Clear stale matches up front so the dropdown never shows results for a
    // previous term while the new fetch is in flight (mirrors select2's
    // clear-on-new-query behavior).
    setPdfClientResults([]);
    let active = true;
    const t = setTimeout(async () => {
      try {
        const results = await searchClipPublications(pdfClientTerm.trim());
        if (active) { setPdfClientResults(results); setPdfClientOpen(true); }
      } catch { if (active) setPdfClientResults([]); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [pdfClientTerm, pdfNewClient, pdfClient]);

  // The Client section is required (matching legacy): a PDF needs either a chosen
  // existing client, or a new client with both name and homepage URL.
  const pdfClientValid = pdfNewClient
    ? pdfNewClientName.trim().length > 0 && pdfNewClientUrl.trim().length > 0
    : pdfClient !== null;

  const handlePdfImport = async () => {
    if (!pdfFile || !pdfClientValid) return;
    setImporting(true);
    try {
      const publication = pdfNewClient
        ? { newPublication: { name: pdfNewClientName.trim(), homepageUrl: pdfNewClientUrl.trim() } }
        : { clipPublicationId: pdfClient!.id };
      await createProjectFromPdf(pdfFile, pdfTitle.trim() || undefined, publication);
      flash('Project created from PDF', true); closePdfModal(); load();
    } catch (e: any) { flash(e.response?.data?.errors?.[0] || 'PDF import failed', false); }
    finally { setImporting(false); }
  };

  const openEdit = (p: PortfolioProject) => {
    setEditProject(p);
    setEditForm({ title: p.title, description: p.description || '' });
    setEditUseImage(p.useImage);
    setEditAssets(p.assets || []);
    setEditStoryFormat(p.story_format || '');
    setEditSkillIds(p.skill_ids || []);
    setEditTopicIds(p.topic_ids || []);
    setModal('edit');
  };

  const handleEditSave = async () => {
    if (!editProject) return;
    setEditSaving(true);
    try {
      // Parity with classic: the portfolio edit form submits title, description
      // and the Use Image toggle. URL is set at creation (import/scrape) and is
      // NOT editable here — classic's edit form had no URL field. Submitting
      // `url` (esp. "" for no-URL projects) tripped the Clip url-length
      // validation -> 500 (eng-maintenance#20945). Images themselves are
      // attached/removed immediately via their own endpoints (below), matching
      // legacy where upload fired on drop, not on Save.
      // story_format / skill_ids / topic_ids restore the classic edit form's
      // Format + Topics + Skills submit (eng-maintenance#21110). The backend has
      // always permitted these in `clip_params` (`:story_format`, `skill_ids: []`,
      // `topic_ids: []`); the migration just stopped sending them. Empty arrays
      // correctly clear all associations (deselect-all), and seeding edit state
      // from the project's current ids means an untouched modal re-submits the
      // existing selection unchanged.
      await updateProject(editProject.id, {
        title: editForm.title,
        description: editForm.description,
        use_image: editUseImage,
        story_format: editStoryFormat,
        skill_ids: editSkillIds,
        topic_ids: editTopicIds,
      });
      flash('Project updated', true); setModal(null); setEditProject(null); load();
    } catch { flash('Failed to update', false); }
    finally { setEditSaving(false); }
  };

  // Image upload fires immediately on drop (legacy parity — assets were saved
  // on upload, not on form submit). Re-fetch the project to pick up the new
  // asset id/thumbnail.
  const refreshEditAssets = useCallback(async (projectId: number) => {
    // Only sync the attached-asset list from the server — NOT the Use Image
    // toggle. `editUseImage` is unsaved form state owned by the user until they
    // hit Save; overwriting it here would silently revert a toggle they flipped
    // before uploading.
    // Fetch the same breadth that's on screen — a project past the first 100 is
    // only reachable after "Load more", so a bare first-page fetch would never
    // contain it and the asset list would never refresh (Bugbot #21323).
    const fresh = (await fetchPortfolioProjects({ limit: loadedPagesRef.current * PROJECTS_PER_PAGE }))
      .find(p => p.id === projectId);
    if (fresh) setEditAssets(fresh.assets || []);
  }, []);

  const handleImageUpload = async (fileOrList: File | FileList) => {
    if (!editProject) return;
    const files = fileOrList instanceof FileList ? Array.from(fileOrList) : [fileOrList];
    if (!files.length) return;
    setImgBusy(true);
    try {
      for (const file of files) {
        await uploadProjectImage(editProject.id, file);
      }
      flash('Image uploaded', true);
    } catch { flash('Failed to upload image', false); }
    finally {
      // Always refresh: on a partial failure (e.g. file 3 of 5 fails) the
      // earlier files are already attached server-side, so the modal must show
      // every confirmed upload — not omit them behind the error.
      try { await refreshEditAssets(editProject.id); } catch { /* keep current list */ }
      load();
      setImgBusy(false);
    }
  };

  const handleImageDelete = async (assetId: number) => {
    if (!editProject) return;
    setImgBusy(true);
    try {
      await deleteProjectImage(editProject.id, assetId);
      setEditAssets(prev => prev.filter(a => a.id !== assetId));
      flash('Image removed', true);
      load();
    } catch { flash('Failed to remove image', false); }
    finally { setImgBusy(false); }
  };

  // useCallback so DragDropContext gets a stable onDragEnd reference (recreated
  // only when `projects` changes) instead of a new closure every render, and so
  // the closure always reads the current `projects` order. `reorderInFlight` is
  // a ref (stable) and `load` is memoized, so `projects` is the only real dep.
  const handleDragEnd = useCallback(async (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    // Drop this drag if a prior reorder hasn't returned yet — overlapping
    // POSTs deadlock the backend (HB 131767363). The final on-screen order is
    // still saved: the next completed drag posts the full ordered list.
    if (reorderInFlight.current) return;
    const reordered = Array.from(projects);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setProjects(reordered);
    reorderInFlight.current = true;
    try {
      await reorderProjects(reordered.map(p => p.id));
    } catch (error: unknown) {
      logError(error instanceof Error ? error : new Error(`Portfolio projects reorder failed: ${error}`));
      flash('Failed to save order', false);
      load();
    } finally {
      reorderInFlight.current = false;
    }
  }, [projects, load]);

  const allSel = projects.length > 0 && selected.size === projects.length;

  return (
    <EditPortfolioLayout>
      <PageTitle>Projects</PageTitle>
      <Help>Keep your profile up-to-date to help us match with clients. <a href="https://contently-support.kayako.com/article/927-optimizing-existing-content-for-organic-search-visibility" target="_blank" rel="noopener noreferrer">Click here</a> to learn more about optimizing your portfolio.</Help>

      <Toolbar>
        <Chk checked={allSel} onChange={e => handleSelectAll(e.target.checked)} />
        <DropWrap onClick={e => e.stopPropagation()}>
          <DropBtn onClick={() => setActOpen(v => !v)}>Actions</DropBtn>
          <DropMenu $v={actOpen}>
            <DropItem onClick={() => handleBulk('move-to-top')}>Move to top</DropItem>
            <DropItem onClick={() => handleBulk('send-to-bottom')}>Send to bottom</DropItem>
            <DropItem onClick={() => handleBulk('delete')}>Delete</DropItem>
          </DropMenu>
        </DropWrap>
        <ToolRight>
          <DropWrap onClick={e => e.stopPropagation()}>
            <DropBtn $p onClick={() => setAddOpen(v => !v)}>Add Project</DropBtn>
            <DropMenu $v={addOpen} style={{ right: 0, left: 'auto' }}>
              <DropItem onClick={() => { setAddOpen(false); setModal('behance'); }}>Import from Behance</DropItem>
              <DropItem onClick={() => { setAddOpen(false); setModal('url'); }}>Import from a client's website</DropItem>
              <DropItem onClick={() => { setAddOpen(false); setModal('url'); }}>Import from a single URL</DropItem>
              <DropItem onClick={() => { setAddOpen(false); setModal('pdf'); }}>Import a PDF</DropItem>
            </DropMenu>
          </DropWrap>
        </ToolRight>
      </Toolbar>

      {projects.length === 0 ? <Empty>No projects yet. Add a project to get started.</Empty> : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="projects">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {projects.map((p, index) => (
                  <Draggable key={String(p.id)} draggableId={String(p.id)} index={index}>
                    {(dragProvided, snapshot) => (
                      <ProjRow
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        $isDragging={snapshot.isDragging}
                      >
                        <ProjHead>
                          <DragHandle {...dragProvided.dragHandleProps} title="Drag to reorder">⠿</DragHandle>
                          <Chk checked={selected.has(p.id)} onChange={e => handleSelect(p.id, e.target.checked)} />
                          <Expand $e={expandedId === p.id} onClick={() => setExpandedId(prev => prev === p.id ? null : p.id)}>▶</Expand>
                          <ProjInfo>
                            <ProjName>{p.title}</ProjName>
                            {p.url && <ProjSrc>{extractDomain(p.url)}</ProjSrc>}
                            {p.clipPublication?.name && <ProjSrc>{p.clipPublication.name}</ProjSrc>}
                          </ProjInfo>
                          <ProjAct>
                            <IcoBtn title="Edit" onClick={() => openEdit(p)}>✏️</IcoBtn>
                            <VisDrop onClick={e => e.stopPropagation()}>
                              <VisBtn onClick={() => setVisOpenId(prev => prev === p.id ? null : p.id)} title={p.public ? 'Visible to everyone' : 'Contently only'}>{p.public ? '👥' : '🔒'}</VisBtn>
                              <VisMenu $v={visOpenId === p.id}>
                                <VisItem $a={!p.public} onClick={() => handleVisibility(p.id, false)}>Contently only</VisItem>
                                <VisItem $a={p.public} onClick={() => handleVisibility(p.id, true)}>Everyone</VisItem>
                              </VisMenu>
                            </VisDrop>
                            <IcoBtn title="Delete" onClick={() => handleDelete(p.id)}>✕</IcoBtn>
                          </ProjAct>
                        </ProjHead>
                        {expandedId === p.id && (
                          <Expanded>
                            {p.url && <ExpSec><ExpLabel>Project</ExpLabel><ExpLink href={p.url} target="_blank" rel="noopener noreferrer">{p.url}</ExpLink></ExpSec>}
                            {/* Format / Description / Topics / Skills — eng-maintenance#21110.
                                The migration dropped these from the expanded row (it showed
                                only Project/PDF/Images). Classic (projects/.../show.js.hamlc)
                                rendered them as plain-text rows; the format value is humanized
                                via the option catalog, topics/skills are the payload name
                                arrays. Restored in the classic label order (Format, PDF,
                                Description, Topics, Skills, Images). */}
                            {p.story_format && (
                              <ExpSec>
                                <ExpLabel>Format</ExpLabel>
                                <ExpText>{options.storyFormats.find(f => f.id === p.story_format)?.name || p.story_format}</ExpText>
                              </ExpSec>
                            )}
                            {/* PDF-backed (uploaded) clip — eng-maintenance#21094. The
                                migration dropped this row, so PDF projects showed only
                                "Images". Legacy (classic projects/.../show.js.hamlc) rendered
                                a "PDF" row = file icon + external_file_name as PLAIN TEXT
                                (not a link), gated on external_file independently of url.
                                Restore exactly that — no clickable file URL, matching classic. */}
                            {p.external_file && p.external_file_name && (
                              <ExpSec>
                                <ExpLabel>PDF</ExpLabel>
                                <PdfFile><PdfIcon>📄</PdfIcon>{p.external_file_name}</PdfFile>
                              </ExpSec>
                            )}
                            {p.description && <ExpSec><ExpLabel>Description</ExpLabel><ExpText>{p.description}</ExpText></ExpSec>}
                            {p.topics.length > 0 && <ExpSec><ExpLabel>Topics</ExpLabel><ExpText>{p.topics.join(', ')}</ExpText></ExpSec>}
                            {p.skills.length > 0 && <ExpSec><ExpLabel>Skills</ExpLabel><ExpText>{p.skills.join(', ')}</ExpText></ExpSec>}
                            {p.imageUrl && <ExpSec><ExpLabel>Images</ExpLabel><ExpImg src={p.imageUrl} alt={p.title} /></ExpSec>}
                          </Expanded>
                        )}
                      </ProjRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* "Load more" — restores access to projects past the first 100, which the
          migration left unreachable (eng-maintenance#21323). Shown only while
          fewer than the total are loaded (legacy: hasNextPage = total_count > size). */}
      {projects.length < totalCount && (
        <LoadMoreWrap>
          <LoadMoreBtn onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </LoadMoreBtn>
        </LoadMoreWrap>
      )}

      {/* Import-from-URL Modal */}
      {modal === 'url' && (
        <Modal onClick={closeImportModal}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Import from URL</ModalTitle>
            <ModalLabel>Project URL</ModalLabel>
            <ModalInput placeholder="https://example.com/article" value={importUrl} onChange={e => setImportUrl(e.target.value)} autoFocus />
            <ModalBtns><MBtn onClick={closeImportModal}>Cancel</MBtn><MBtn $p onClick={handleImport} disabled={importing || !importUrl.trim()}>{importing ? 'Importing...' : 'Import'}</MBtn></ModalBtns>
          </ModalBox>
        </Modal>
      )}

      {/* parity-audit-r2 (L6): Import-from-Behance Modal — username search → pick →
          import via behance_id (legacy two-step flow). */}
      {modal === 'behance' && (
        <Modal onClick={closeBehanceModal}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Import from Behance</ModalTitle>
            <ModalLabel>Behance username</ModalLabel>
            <ModalInput
              placeholder="Enter your Behance username"
              value={behanceUsername}
              onChange={e => setBehanceUsername(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleBehanceSearch(); }}
              autoFocus
            />
            <ModalBtns>
              <MBtn onClick={closeBehanceModal}>Cancel</MBtn>
              <MBtn $p onClick={handleBehanceSearch} disabled={behanceSearching || !behanceUsername.trim()}>
                {behanceSearching ? 'Searching...' : 'Search'}
              </MBtn>
            </ModalBtns>

            {behanceResults && behanceResults.length > 0 && (
              <>
                <ModalLabel style={{ marginTop: 16 }}>Select projects to import</ModalLabel>
                <BehanceResultList>
                  {behanceResults.map(p => (
                    <BehanceResultItem key={p.id}>
                      <input
                        type="checkbox"
                        checked={behanceSelectedIds.has(p.id)}
                        onChange={() => toggleBehanceSelected(p.id)}
                      />
                      <span>{p.title}</span>
                    </BehanceResultItem>
                  ))}
                </BehanceResultList>
                <ModalBtns>
                  <MBtn onClick={closeBehanceModal}>Cancel</MBtn>
                  <MBtn $p onClick={handleBehanceImport} disabled={behanceImporting || behanceSelectedIds.size === 0}>
                    {behanceImporting ? 'Importing...' : `Import ${behanceSelectedIds.size || ''}`.trim()}
                  </MBtn>
                </ModalBtns>
              </>
            )}
          </ModalBox>
        </Modal>
      )}

      {/* Manual-entry fallback Modal (eng-maintenance#20455). Shown when URL
          auto-extraction fails fast (503) — classic dropped the user here
          seeded with the URL so they could still create the project by hand.
          Field set mirrors classic's manual card: URL (read-only) +
          Title/Format/Description/Topics/Skills, all required. */}
      {modal === 'manual' && (
        <Modal onClick={closeManualModal}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Add project details</ModalTitle>
            <ManualNotice>We were unable to collect the information needed for this project. Please add the missing details below.</ManualNotice>

            <ModalLabel>Project URL</ModalLabel>
            <ManualUrl translate="no" href={manualUrl} target="_blank" rel="noopener noreferrer">{manualUrl}</ManualUrl>

            <ModalLabel>Title</ModalLabel>
            <ModalInput value={manualTitle} onChange={e => setManualTitle(e.target.value)} placeholder="The title of your project" maxLength={600} autoFocus />

            <ModalLabel>Format</ModalLabel>
            <ModalSelect value={manualStoryFormat} onChange={e => setManualStoryFormat(e.target.value)}>
              <option value="">Select a format</option>
              {options.storyFormats.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </ModalSelect>

            <ModalLabel>Description</ModalLabel>
            <ModalTextarea value={manualDescription} onChange={e => setManualDescription(e.target.value)} placeholder="Please provide a brief summary of the project." maxLength={600} />

            <TagMultiSelect label="Topics" options={options.topics} selectedIds={manualTopicIds} onChange={setManualTopicIds} />
            <TagMultiSelect label="Skills" options={options.skills} selectedIds={manualSkillIds} onChange={setManualSkillIds} />

            <ModalBtns>
              <MBtn onClick={closeManualModal}>Cancel</MBtn>
              <MBtn $p onClick={handleManualSave} disabled={importing || !manualValid}>{importing ? 'Saving...' : 'Save project'}</MBtn>
            </ModalBtns>
          </ModalBox>
        </Modal>
      )}

      {/* Import a PDF Modal (eng-maintenance#21068) */}
      {modal === 'pdf' && (
        <Modal onClick={closePdfModal}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Import a PDF</ModalTitle>

            {/* Client/Publication — required, matching the legacy PDF form. The
                backend resolves a publication for every non-URL clip; without
                this the create 500s (ClipPublicationCreationError). */}
            {!pdfNewClient ? (
              <>
                <ModalLabel>Client</ModalLabel>
                {pdfClient ? (
                  <ClientChosen>
                    <span>{pdfClient.name}</span>
                    <LinkBtn type="button" onClick={() => { setPdfClient(null); setPdfClientTerm(''); }}>Change</LinkBtn>
                  </ClientChosen>
                ) : (
                  <ClientSearch>
                    <ModalInput
                      placeholder="Start typing to find the client or publication"
                      value={pdfClientTerm}
                      onChange={e => setPdfClientTerm(e.target.value)}
                      onFocus={() => { if (pdfClientResults.length) setPdfClientOpen(true); }}
                      style={{ marginBottom: 0 }}
                    />
                    {pdfClientOpen && pdfClientResults.length > 0 && (
                      <ClientResults>
                        {pdfClientResults.map(r => (
                          <ClientResultItem
                            key={r.id}
                            onClick={() => { setPdfClient(r); setPdfClientOpen(false); setPdfClientResults([]); }}
                          >
                            {r.name}
                          </ClientResultItem>
                        ))}
                      </ClientResults>
                    )}
                  </ClientSearch>
                )}
                {!pdfClient && (
                  <div style={{ margin: '6px 0 16px' }}>
                    <LinkBtn type="button" onClick={() => { setPdfNewClient(true); setPdfClientTerm(''); setPdfClientResults([]); setPdfClientOpen(false); }}>
                      Don&apos;t see what you&apos;re looking for? Add a new client
                    </LinkBtn>
                  </div>
                )}
              </>
            ) : (
              <>
                <ModalLabel>Client name</ModalLabel>
                <ModalInput placeholder="Name of client or publication" value={pdfNewClientName} onChange={e => setPdfNewClientName(e.target.value)} />
                <ModalLabel>Client homepage URL</ModalLabel>
                <ModalInput placeholder="https://example.com" value={pdfNewClientUrl} onChange={e => setPdfNewClientUrl(e.target.value)} />
                <div style={{ margin: '-6px 0 16px' }}>
                  <LinkBtn type="button" onClick={() => { setPdfNewClient(false); setPdfNewClientName(''); setPdfNewClientUrl(''); }}>
                    Choose an existing client instead
                  </LinkBtn>
                </div>
              </>
            )}

            <ModalLabel>Title (optional)</ModalLabel>
            <ModalInput placeholder="My PDF project" value={pdfTitle} onChange={e => setPdfTitle(e.target.value)} />
            <ModalLabel>PDF file</ModalLabel>
            <Dropzone>
              <FileUploader
                handleChange={handlePdfSelect}
                name="file"
                types={PDF_FILE_TYPES}
                hoverTitle="Drop your PDF here..."
                disabled={importing}
              >
                <div>{pdfFile ? pdfFile.name : 'Click or drag a PDF here to upload'}</div>
              </FileUploader>
            </Dropzone>
            <ModalBtns>
              <MBtn onClick={closePdfModal}>Cancel</MBtn>
              <MBtn $p onClick={handlePdfImport} disabled={importing || !pdfFile || !pdfClientValid}>{importing ? 'Importing...' : 'Import'}</MBtn>
            </ModalBtns>
          </ModalBox>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === 'edit' && editProject && (
        <Modal onClick={() => { setModal(null); setEditProject(null); }}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Edit Project</ModalTitle>
            <ModalLabel>Title</ModalLabel>
            <ModalInput value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />

            {/* Format / Topics / Skills — eng-maintenance#21110. Restores the
                classic edit form's Format select + Topics/Skills multi-selects,
                in the classic field order (Title, Format, Description, Topics,
                Skills). Options come from the projects.json `options` catalog. */}
            <ModalLabel>Format</ModalLabel>
            <ModalSelect value={editStoryFormat} onChange={e => setEditStoryFormat(e.target.value)}>
              <option value="">Select a format</option>
              {options.storyFormats.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </ModalSelect>

            <ModalLabel>Description</ModalLabel>
            <ModalTextarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />

            <TagMultiSelect label="Topics" options={options.topics} selectedIds={editTopicIds} onChange={setEditTopicIds} />
            <TagMultiSelect label="Skills" options={options.skills} selectedIds={editSkillIds} onChange={setEditSkillIds} />

            <ModalLabel>Images</ModalLabel>
            <UseImageRow>
              <Chk checked={editUseImage} onChange={e => setEditUseImage(e.target.checked)} />
              Use Image
            </UseImageRow>
            {editAssets.length > 0 && (
              <ImageGrid>
                {editAssets.map(a => (
                  <ImageThumb key={a.id}>
                    <ThumbImg src={a.small} alt="Project image" />
                    <ThumbDel
                      type="button"
                      title="Remove image"
                      disabled={imgBusy}
                      onClick={() => handleImageDelete(a.id)}
                    >×</ThumbDel>
                  </ImageThumb>
                ))}
              </ImageGrid>
            )}
            <Dropzone>
              <FileUploader
                handleChange={handleImageUpload}
                name="file"
                multiple
                types={IMAGE_FILE_TYPES}
                hoverTitle="Drop files here..."
                disabled={imgBusy}
              >
                <div>{imgBusy ? 'Uploading...' : 'Click or drag files here to upload'}</div>
              </FileUploader>
            </Dropzone>

            <ModalBtns>
              <MBtn onClick={() => { setModal(null); setEditProject(null); }}>Cancel</MBtn>
              <MBtn $p onClick={handleEditSave} disabled={editSaving || !editForm.title.trim()}>{editSaving ? 'Saving...' : 'Save'}</MBtn>
            </ModalBtns>
          </ModalBox>
        </Modal>
      )}

      {toast && <Toast $ok={toast.ok}>{toast.msg}</Toast>}
    </EditPortfolioLayout>
  );
}

export default PortfolioProjectsPage;
